import { useState, useCallback, useEffect, useRef } from "react";
import bodycamClient from "../../../api/clients/bodycamClient";
import { Dayjs } from "dayjs";
import { VideoPreview } from "../../../shared/interfaces/VideoPreview";
import { EvidenceFileDDB } from "../../../shared/interfaces/EvidenceFileDDB";
import { EvidenceListResponse } from "../../../shared/interfaces/EvidenceListResponse";
import { VideoStatus } from "../../../shared/interfaces/VideoData";
import { useToast } from "../../../shared/toast";
import { isPlausibleRecordedAtMs } from "../utils/fileMetadata";

/** Which slice of evidence the page is showing: ingested videos or AI-filtered
 *  (rejected / never-fetched) candidates. Maps to the `videoState` query param. */
export type EvidenceTab = "ingested" | "unfetched";

export const getDisplayRecordedAtMs = (evidence: EvidenceFileDDB): number =>
  isPlausibleRecordedAtMs(evidence.recordedAt)
    ? evidence.recordedAt
    : evidence.createdAt;

const isVideoStatus = (status: string): status is VideoStatus =>
  (Object.values(VideoStatus) as string[]).includes(status);

/** Validates a backend status string against the VideoStatus enum. An unknown
 *  value is passed through (not coerced to a default) so the status chip still
 *  surfaces what the backend actually sent; downstream switches handle it via
 *  their default branches. */
export const parseVideoStatus = (status: string): VideoStatus => {
  if (!isVideoStatus(status)) {
    console.warn(`Unknown evidence status "${status}"`);
  }
  return status as VideoStatus;
};

// Helper to map API response to VideoPreview
export const mapEvidenceToVideoPreview = (
  evidence: EvidenceFileDDB
): VideoPreview => {
  const status = parseVideoStatus(evidence.status);
  const isFiltered = status === VideoStatus.NOT_EVIDENCE;
  return {
    id: evidence.evidenceId,
    thumbnailUrl: evidence.file, // Using file URL as thumbnail (empty when filtered)
    rmsName: evidence.externalId || "N/A",
    // Filtered candidates were never fetched, so they carry no filename yet.
    fileName:
      evidence.fileName || (isFiltered ? "Filtered footage (not fetched)" : ""),
    recordedDate: new Date(getDisplayRecordedAtMs(evidence)).toString(),
    uploadedDate: new Date(evidence.createdAt).toString(),
    officerName: evidence.uploadedByUserId || "System",
    duration: evidence.duration,
    status,
    device: evidence.device || "Bodycam",
    fileType: evidence.fileType,
    judgeReasons: evidence.judgeReasons,
    judgeTier: evidence.judgeTier,
    judgeConfidence: evidence.judgeConfidence,
  };
};

export const useListVideos = (tab: EvidenceTab = "ingested") => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [videos, setVideos] = useState<VideoPreview[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [bodycamVendor, setBodycamVendor] = useState<string | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Pagination state
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);

  // Ref to cancel in-flight requests when a newer one starts
  const abortControllerRef = useRef<AbortController | null>(null);

  // States for date range filter
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);

  // State for sorting
  const [sortBy, setSortBy] = useState<"createdAt" | "recordedAt">("createdAt");

  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const toast = useToast();

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  /**
   * Fetches videos from the API.
   * @param token - The pagination token (optional)
   * @param mode  - "replace": swap the whole list (initial / filter change)
   *               "append":  add next page to the end (loadMore)
   *               "merge":   update known rows in-place and prepend new items,
   *                          preserving the pagination cursor (polling)
   */
  const fetchVideos = useCallback((token?: string, mode: "replace" | "append" | "merge" = "replace") => {
    // Cancel any in-flight request so stale responses are ignored
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoading(true);

    const params: any = {
      nextToken: token,
      sortBy: sortBy,
      videoState: tab === "unfetched" ? "rejected" : "ingested",
    };

    // Filters are disabled on the unfetched tab, so don't apply their values
    // there — otherwise a search/date set on the Ingested tab would silently
    // filter a list whose controls are locked.
    const filtersApply = tab === "ingested";

    if (filtersApply && dateFrom) {
      params.dateFrom = dateFrom.startOf('day').toISOString();
    }

    if (filtersApply && dateTo) {
      params.dateTo = dateTo.endOf('day').toISOString();
    }

    if (filtersApply && debouncedSearchTerm) {
      params.textQuery = debouncedSearchTerm;
    }

    bodycamClient
      .get<EvidenceListResponse>("/evidence", { params, signal: controller.signal })
      .then((response) => {
        if (controller.signal.aborted) return;

        const data = response.data;
        const newVideos = (data.evidence || []).map(mapEvidenceToVideoPreview);

        if (mode === "replace") {
          setVideos(newVideos);
          setNextToken(data.nextToken);
          setHasMore(!!data.nextToken);
        } else if (mode === "append") {
          setVideos((prev) => [...prev, ...newVideos]);
          setNextToken(data.nextToken);
          setHasMore(!!data.nextToken);
        } else {
          // merge: update known rows in-place, prepend genuinely new items.
          // nextToken/hasMore are intentionally left unchanged so the pagination
          // cursor for loaded pages is not overwritten by a page-1 poll.
          setVideos((prev) => {
            const prevIds = new Set(prev.map((v) => v.id));
            const toAdd = newVideos.filter((v) => !prevIds.has(v.id));
            const merged = prev.map((existing) => {
              const fresh = newVideos.find((v) => v.id === existing.id);
              if (!fresh) return existing;
              // Keep an optimistically-FETCHING card (set by markVideoFetching)
              // until the backend stops reporting it as NOT_EVIDENCE.
              if (
                existing.status === VideoStatus.FETCHING &&
                fresh.status === VideoStatus.NOT_EVIDENCE
              ) {
                return existing;
              }
              // Take the whole fresh row, not just status — ingestion fills in
              // fileName, duration, file URL, etc. as a video progresses.
              return { ...existing, ...fresh };
            });
            // On the unfetched tab, a locally-FETCHING row absent from the poll
            // response means the backend has transitioned it out of the rejected
            // set — drop it so it only shows on the Ingested tab. Absence alone
            // isn't enough for other rows: page-2+ rows are also absent from a
            // page-1 poll, so non-FETCHING rows are never removed here (a video
            // fetched from another session lingers until the next replace).
            const freshIds = new Set(newVideos.map((v) => v.id));
            const result =
              tab === "unfetched"
                ? merged.filter(
                    (v) =>
                      v.status !== VideoStatus.FETCHING || freshIds.has(v.id),
                  )
                : merged;
            return toAdd.length ? [...toAdd, ...result] : result;
          });
        }

        if (data.bodycamVendor) {
          setBodycamVendor(data.bodycamVendor);
        }
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        toast.error(error.response?.data?.error || "Failed to fetch videos");
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
          setIsInitialLoad(false);
        }
      });
  }, [dateFrom, dateTo, debouncedSearchTerm, sortBy, tab, toast]);

  /**
   * Optimistically flips a video to FETCHING after its fetch was requested, so
   * the card stays visible and shows the in-progress state immediately. The
   * 10-second merge poll reconciles the real status once the backend catches up.
   */
  const markVideoFetching = useCallback((videoId: string) => {
    setVideos((prev) =>
      prev.map((v) =>
        v.id === videoId ? { ...v, status: VideoStatus.FETCHING } : v
      )
    );
  }, []);

  /**
   * Loads the next page of videos if available.
   */
  const loadMore = useCallback(() => {
    if (hasMore && !isLoading) {
      fetchVideos(nextToken, "append");
    }
  }, [hasMore, isLoading, fetchVideos, nextToken]);

  // Clear the list on tab switch so the grid shows skeletons while the new
  // tab's first page loads, instead of the previous tab's stale cards.
  useEffect(() => {
    setVideos([]);
    setIsInitialLoad(true);
  }, [tab]);

  // Initial load and re-fetch when date filters / active tab change
  useEffect(() => {
    setNextToken(undefined);
    setHasMore(false);
    fetchVideos(undefined, "replace");
    return () => { abortControllerRef.current?.abort(); };
  }, [dateFrom, dateTo, debouncedSearchTerm, sortBy, fetchVideos]);

  // Poll every 10 seconds using merge mode so appended pages are preserved
  // and the pagination cursor is not overwritten by a page-1 response.
  useEffect(() => {
    const interval = setInterval(() => {
      fetchVideos(undefined, "merge");
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchVideos]);

  return {
    videos,
    isLoading,
    isInitialLoad,
    bodycamVendor,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    sortBy,
    setSortBy,
    fetchVideos: () => fetchVideos(undefined, "replace"),
    markVideoFetching,
    loadMore,
    hasMore,
    searchTerm,
    setSearchTerm,
    handleSearchChange
  };
};
